import React from "react";
import { Link } from "react-router-dom";
import { Briefcase, DollarSign, MapPin, FileText, Calendar } from "lucide-react";
import { formatCOP, formatDate } from "../lib/apiClient";

/**
 * CaseSummaryCard — Tarjeta compacta con datos clave del caso.
 *
 * Iter20 — Visible al tope de páginas del flujo (Prevalidación, Diagnóstico, Kit).
 * Reduce scroll y orienta al usuario sin que tenga que volver al dashboard.
 *
 * Props:
 *  - caseDoc: case data
 *  - showLink: bool — incluir link a la página de detalle del caso (default true)
 */
const MODALIDAD_LABEL = {
  minima_cuantia: "Mínima cuantía",
  seleccion_abreviada: "Selección abreviada",
  licitacion_publica: "Licitación pública",
  concurso_meritos: "Concurso de méritos",
  contratacion_directa: "Contratación directa",
  desconocida: "No determinada",
};

const STATE_BADGE = {
  draft_intake: { label: "Borrador", cls: "bg-gray-100 text-gray-700" },
  nivel0_pending: { label: "Prevalidación", cls: "bg-amber-50 text-amber-800" },
  nivel0_ready: { label: "Prevalidación lista", cls: "bg-emerald-50 text-emerald-800" },
  out_of_scope: { label: "Fuera de alcance", cls: "bg-indigo-50 text-indigo-800" },
  nivel1_payment_pending: { label: "Pago pendiente N1", cls: "bg-amber-50 text-amber-800" },
  nivel1_paid: { label: "Pagado N1", cls: "bg-emerald-50 text-emerald-800" },
  nivel1_processing: { label: "Procesando", cls: "bg-blue-50 text-blue-800" },
  nivel1_needs_human_review: { label: "En revisión", cls: "bg-violet-50 text-violet-800" },
  nivel1_revision_requested: { label: "Revisión solicitada", cls: "bg-violet-50 text-violet-800" },
  nivel1_delivered: { label: "Diagnóstico listo", cls: "bg-emerald-50 text-emerald-800" },
  nivel2_offer_sent: { label: "Oferta Kit", cls: "bg-amber-50 text-amber-800" },
  nivel2_payment_pending: { label: "Pago pendiente N2", cls: "bg-amber-50 text-amber-800" },
  nivel2_delivered: { label: "Kit entregado", cls: "bg-emerald-50 text-emerald-800" },
  closed: { label: "Cerrado", cls: "bg-gray-100 text-gray-700" },
};

export default function CaseSummaryCard({ caseDoc, showLink = true }) {
  if (!caseDoc) return null;
  const badge = STATE_BADGE[caseDoc.state] || { label: caseDoc.state || "—", cls: "bg-gray-100 text-gray-700" };
  return (
    <div
      className="bg-white border border-gray-200 rounded-sm p-4 sm:p-5 mb-5"
      data-testid="case-summary-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">Caso</p>
          <h2
            className="font-serif text-lg sm:text-xl text-gray-900 truncate"
            title={caseDoc.title || caseDoc.id}
            data-testid="case-summary-title"
          >
            {caseDoc.title || "Sin título"}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-sm ${badge.cls}`}
            data-testid="case-summary-state-badge"
          >
            {badge.label}
          </span>
          {showLink && (
            <Link
              to={`/cases/${caseDoc.id}`}
              className="text-[11px] uppercase tracking-wider text-brand-burgundy hover:underline"
              data-testid="case-summary-detail-link"
            >
              Detalle →
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <Cell icon={<Briefcase className="w-3.5 h-3.5 text-gray-400" />} label="Entidad">
          <span className="truncate">{caseDoc.entity_name || "—"}</span>
        </Cell>
        <Cell icon={<DollarSign className="w-3.5 h-3.5 text-gray-400" />} label="Valor estimado">
          <span className="tabular-nums">
            {caseDoc.valor_estimado_cop ? formatCOP(caseDoc.valor_estimado_cop) : "—"}
          </span>
        </Cell>
        <Cell icon={<FileText className="w-3.5 h-3.5 text-gray-400" />} label="Modalidad">
          <span className="truncate">{MODALIDAD_LABEL[caseDoc.modalidad] || caseDoc.modalidad || "—"}</span>
        </Cell>
        <Cell icon={<MapPin className="w-3.5 h-3.5 text-gray-400" />} label="Departamento">
          <span className="truncate">{caseDoc.departamento || "—"}</span>
        </Cell>
      </div>

      {caseDoc.created_at && (
        <p className="text-[10px] text-gray-400 mt-3 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          Creado {formatDate(caseDoc.created_at)}
          {caseDoc.updated_at && caseDoc.updated_at !== caseDoc.created_at && (
            <> · Actualizado {formatDate(caseDoc.updated_at)}</>
          )}
        </p>
      )}
    </div>
  );
}

function Cell({ icon, label, children }) {
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500 mb-0.5">
        {icon}
        {label}
      </div>
      <div className="text-sm text-gray-900 truncate">{children}</div>
    </div>
  );
}
